import React, { useState, useEffect } from 'react';
import MetaTags from 'react-meta-tags';
import { Col, Container, Row, FormGroup, Alert } from 'reactstrap';
import { AvForm, AvField } from 'availity-reactstrap-validation';

import { calculatorApi } from '../../services/CalculatorService';

import Breadcrumbs from '../../components/Breadcrumb';
import PageLoader from '../../components/Loaders/newPageLoader';
import useLoadingProgress from '../../hooks/useLoadingProgress';

import box_weight from '../../assets/images/box-weight.png';
import fedex from '../../assets/images/calculator/fedex_icon.png';
import ems from '../../assets/images/calculator/ems_icon.png';
import delivery_courier from '../../assets/images/calculator/delivery_courier.png';
import calculate_tax from '../../assets/images/calculator/calculate_tax.png';
import ups from '../../assets/images/calculator/ups_icon.png';

export default function CalculatorEurope({ profileData, loading }) {
  const {
    data = [],
    isLoading,
    isFetching,
    error,
    refetch
  } = calculatorApi.useFetchAllDataQuery(undefined, {
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false
  });

  const [destinations, setDestinationsData] = useState([]);
  const [fedexPrices, setFedexPricesData] = useState([]);
  const [emsPrices, setEmsPricesData] = useState([]);
  const [agents, setAgentsData] = useState([]);
  const [agentFees, setAgentFeesData] = useState([]);
  const [boxSizes, setBoxSizesData] = useState([]);
  const [countriesTrs, setCountriesTrsData] = useState([]);
  const [taxPrices, setTaxPricesData] = useState([]);
  const [variables, setVariablesData] = useState([]);

  const { progress, step, visible } = useLoadingProgress({ isLoading, isFetching });
  useEffect(() => {
    if (data.destinations) {
      setDestinationsData(JSON.parse(JSON.stringify(data.destinations)));
      setFedexPricesData(JSON.parse(JSON.stringify(data.fedexPrices)));
      setEmsPricesData(JSON.parse(JSON.stringify(data.emsPrices)));
      setAgentsData(JSON.parse(JSON.stringify(data.agents)));
      setAgentFeesData(JSON.parse(JSON.stringify(data.agentFees)));
      setBoxSizesData(JSON.parse(JSON.stringify(data.boxSizes)));
      setCountriesTrsData(JSON.parse(JSON.stringify(data.countriesTrs)));
      setTaxPricesData(JSON.parse(JSON.stringify(data.taxPrices)));
      setVariablesData(JSON.parse(JSON.stringify(data.variables)));
    }
  }, [data]);

  const [formState, setFormState] = useState({
    volumeWeight: 0,
    L: '',
    W: '',
    H: '',
    boxSize: '',
    boxSizeL: '',
    boxSizeW: '',
    boxSizeH: '',
    boxSizeLTr: '',
    boxSizeWTr: '',
    boxSizeHTr: '',
    selectedGrossWeight: '',
    boxSizeVolumeWeight: '',
    tax: 0,
  });
  const [isNewBox, setIsNewBox] = useState(false);
  const toggleNewIsNewBox = () => {
    setIsNewBox(prevState => !prevState);
  };
  const handleCalculate = (e) => {
    const { name, value } = e.target;
    if (isNewBox) {
      if (name === 'L' || name === 'W') {
        setFormState(prev => ({ ...prev, [name]: value }));
        return;
      }
      const L = Number(formState.L) || 0;
      const W = Number(formState.W) || 0;
      const H = Number(value) || 0;
      const vw = (L * W * H) / 5000;
      const ceilVW = Math.ceil(vw);
      const match = boxSizes.find(el =>
        parseInt(ceilVW, 10) <= parseInt(el.volWeight, 10)
      );
      setFormState(prev => ({
        ...prev,
        H: value,
        volumeWeight: ceilVW,
        boxSize: match ? match.boxSizeNo : null,
        selectedGrossWeight: ''
      }));
    } else {
      const [boxNoStr = '', volStr = '0'] = String(value).split('/');
      const boxNo = parseInt(boxNoStr, 10);
      const vol = parseInt(volStr, 10);
      const selected = boxSizes.find(v => v.boxSizeNo === boxNo);
      setFormState(prev => ({
        ...prev,
        boxSizeL: selected?.length ?? prev.boxSizeL,
        boxSizeW: selected?.width ?? prev.boxSizeW,
        boxSizeH: selected?.height ?? prev.boxSizeH,
        boxSize: boxNoStr,
        volumeWeight: vol,
        boxSizeVolumeWeight: vol,
        display: false,
        selectedGrossWeight: ''
      }));
    }
  };
  const handleNetWeightCalc = (e) => {
    const [grossStr = "0"] = String(e.target.value).split("/");
    const gross = parseInt(grossStr, 10);
    setFormState(prev => {
      const base = Number(prev.boxSizeVolumeWeight) || 0;
      const volumeWeight = Number.isFinite(gross) ? Math.max(gross, base) : base;

      return {
        ...prev,
        volumeWeight,
        display: false,
      };
    });
  };
  const handleValidCalculate = (e, values) => {
    setFormState(prev => {
      const boxNo = parseInt(prev.boxSize, 10) || 0;
      const vw = Number(prev.volumeWeight) || 0;

      const taxRow = taxPrices.find(
        t => values['agentCountryPrice'] === t.country && boxNo === Number(t.boxNo)
      );
      const taxPrice = Number(taxRow?.price) || 0;

      const fedexRow = fedexPrices.find(
        f => values['agentCountryPrice'] === f.country && vw === Number(f.netWeightFedex)
      );
      const priceAmountFedex = Number(fedexRow?.priceAmountFedex) || 0;

      const emsRow = emsPrices.find(
        e => values['agentCountryPrice'] === e.country && vw === Number(e.netWeightEms)
      );
      const priceAmountEms = Number(emsRow?.priceAmountEms) || 0;

      // Agent fee: first bracket where vw <= weightKG, else fallback 95
      const feeRow = agentFees.find(a => vw <= Number(a.weightKG));
      const agentFee = Number(feeRow?.agentFee) || 95;

      // Destination-based prices (only set if we find a match)
      const destRow = destinations.find(
        d =>
          values['agentCountryPrice'] == d.countryFrom &&
          values['countryPrice'] == d.countryTo &&
          boxNo == Number(d.box)
      );

      const overAllAmountFedex = priceAmountFedex + agentFee + taxPrice;
      const overAllAmountEms = priceAmountEms + agentFee + taxPrice;

      const next = {
        ...prev,
        tax: taxPrice,
        priceAmountFedex,
        priceAmountEms,
        agentFee,
        overAllAmountFedex,
        overAllAmountEms,
        display: true,
      };

      if (destRow) {
        next.cheap = Number(destRow.cheapPr) || 0;
        next.expensive = Number(destRow.expPr) || 0;
        next.selectedAgentCountry = values['agentCountryPrice'];
        next.selectedCountry = values['countryPrice'];
      }
      return next;
    });
  };
  const agentCountriesWeight = emsPrices.filter((v, i, a) => a.findIndex((v2) => v2.netWeightEms === v.netWeightEms) === i);
  const agentCountries = emsPrices.filter((v, i, a) => a.findIndex((v2) => v2.country === v.country) === i);
  const newcountryPrices = destinations.filter((v, i, a) => a.findIndex((v2) => v2.countryTo === v.countryTo) === i);

  return (
    <React.Fragment>
      <PageLoader progress={progress} step={step} visible={visible} />
      <div className='page-content'>
        <MetaTags>
          <title>Transportation Calculator Through Agent</title>
        </MetaTags>
          <Container fluid>
            {profileData.permissions.includes('shippingCalcAccess') ? (
              <>
              <Breadcrumbs title='Calculator' breadcrumbItem='Calculator Through Agent' />
              <div id='card-section'>
                <Row id='card-section'>
                  <Col lg='2'></Col>
                  <Col lg='8'>
                    <div className='screen'>
                      <AvForm onValidSubmit={handleValidCalculate} style={{ height: '100%' }}>
                        {/* <input type="text" placeholder="0" name="displayResult" className="area"/> */}
                        <Row style={{ height: '100%' }}>
                          <Col lg='6' style={{ height: '100%' }}>
                            <div className="area">
                              {formState.display && formState.overAllAmountFedex && (
                              <>
                                <Row className="ms-2 mt-4 area-text">
                                  <h2 className='text-start area-text-title'><b>Korea <i className='mdi mdi-arrow-right font-size-24'></i> {formState.selectedAgentCountry}</b></h2>
                                  <Col md={12} className='mb-2'>
                                    <Row>
                                      <Col md={1} className='pe-0 text-start'><img src={fedex} width='30' height='30' alt='' /></Col>
                                      <Col md={6} className='ps-1 pt-2 text-start'><h5 className="area-text-label">Transportation Price:</h5></Col>
                                      <Col md={5} className='ps-0 pt-2 text-start'><h5 className="area-text-value"><span className="fedex-grad">{formState.priceAmountFedex}$</span></h5></Col>
                                    </Row>
                                  </Col>
                                  <Col md={12} className='mb-2'>
                                    <Row>
                                      <Col md={1} className='pe-0 text-start'><img src={ems} width='30' height='30' alt='' /></Col>
                                      <Col md={6} className='ps-1 pt-2 text-start'><h5 className="area-text-label">Transportation Price:</h5></Col>
                                      <Col md={5} className='ps-0 pt-2 text-start'><h5 className="area-text-value"><span className="ems-grad">{formState.priceAmountEms}$</span></h5></Col>
                                    </Row>
                                  </Col>
                                  <Col md={12} className='mb-2'>
                                    <Row>
                                      <Col md={1} className='pe-0 text-start'><img src={delivery_courier} width='30' height='30' alt='' /></Col>
                                      <Col md={6} className='ps-1 pt-2 text-start'><h5 className="area-text-label">Agent Fee:</h5></Col>
                                      <Col md={5} className='ps-0 pt-2 text-start'><h5 className="area-text-value">{formState.agentFee}$</h5></Col>
                                    </Row>
                                  </Col>
                                  <Col md={12} className='mb-2'>
                                    <Row>
                                      <Col md={1} className='pe-0 text-start'><img src={calculate_tax} width='30' height='30' alt='' /></Col>
                                      <Col md={6} className='ps-1 pt-2 text-start'><h5 className="area-text-label">Tax Amount:</h5></Col>
                                      <Col md={5} className='ps-0 pt-2 text-start'><h5 className="area-text-value">{formState.tax}$</h5></Col>
                                    </Row>
                                  </Col>
                                  <Col md={12} className='mt-3'>
                                    <Row>
                                      <Col md={3} className='pe-4 text-start'><h5 className="area-text-label">Total Fedex:</h5></Col>
                                      <Col md={4} className='ps-1 pe-0 text-start'><h5 className="area-text-calc"><span className="fedex-grad">{formState.priceAmountFedex}$</span> + {formState.agentFee}$ + {formState.tax}$ = </h5></Col>
                                      <Col md={5} className='ps-0 pe-0 text-start'><h5 className="area-text-value"><span className="fedex-grad">{formState.overAllAmountFedex}$</span></h5></Col>
                                    </Row>
                                    <Row className='mt-2'>
                                      <Col md={3} className='pe-4 text-start'><h5 className="area-text-label">Total EMS:</h5></Col>
                                      <Col md={4} className='ps-1 pe-0 text-start'><h5 className="area-text-calc"><span className="ems-grad">{formState.priceAmountEms}$</span> + {formState.agentFee}$ + {formState.tax}$ = </h5></Col>
                                      <Col md={5} className='ps-0 pe-0 text-start'><h5 className="area-text-value"><span className="ems-grad">{formState.overAllAmountEms}$</span></h5></Col>
                                    </Row>
                                  </Col>
                                  <hr className='area-line mt-4' />
                                </Row>
                                <Row className="ms-2 mt-4 area-text">
                                  <h2 className='text-start pb-2 area-text-title'><b>UPS {formState.selectedAgentCountry} <i className='mdi mdi-arrow-right font-size-24'></i> {formState.selectedCountry}</b></h2>
                                  <Col md={12}>
                                    <Row>
                                      <Col md={2} className="ps-2 pe-0 text-start">
                                        <img src={ups} width="60" height="60" alt="" />
                                      </Col>
                                      <Col md={5} className="ps-3 pt-2 text-start">
                                        <h5 className="area-text-label">Cheap Transport Price:</h5>
                                        <h5 className="area-text-label">Expensive Transport Price:</h5>
                                      </Col>
                                      <Col md={5} className="ps-0 pt-2 text-start">
                                        <h5 className="area-text-value"><span className="cheap">{formState.cheap}$</span></h5>
                                        <h5 className="area-text-value"><span className="exp">{formState.expensive}$</span></h5>
                                      </Col>
                                    </Row>
                                  </Col>
                                  <hr className='area-line mt-4' />
                                </Row>
                                <Row className="ms-2 mt-4 mb-2 area-text">
                                  <h2 className='text-start pb-2 area-text-title'><b>Final Price Korea <i className='mdi mdi-arrow-right font-size-24'></i> {formState.selectedCountry}</b></h2>
                                  <Col md={12} >
                                    <Row>
                                      <Col md={4} className='pe-0 text-start'><h5 className="area-text-label">Fedex + Cheap Price:</h5></Col>
                                      <Col md={3} className='ps-0 pe-0 text-start'><h5 className="area-text-calc"><span className="fedex-grad">{formState.overAllAmountFedex}$</span> + <span className="cheap">{formState.cheap}$</span> =</h5></Col>
                                      <Col md={5} className='ps-0 text-start'><h5 className="area-text-value">{formState.cheap + formState.overAllAmountFedex}$</h5></Col>
                                    </Row>
                                    <Row className='pt-1'>
                                      <Col md={4} className='pe-0 text-start'><h5 className="area-text-label">Fedex + Expensive Price:</h5></Col>
                                      <Col md={3} className='ps-0 pe-0 text-start'><h5 className="area-text-calc"><span className="fedex-grad">{formState.overAllAmountFedex}$</span> + <span className="exp">{formState.expensive}$</span> =</h5></Col>
                                      <Col md={5} className='ps-0 text-start'><h5 className="area-text-value">{formState.expensive + formState.overAllAmountFedex}$</h5></Col>
                                    </Row>
                                  </Col>
                                  <Col md={12} className='mt-4'>
                                    <Row>
                                      <Col md={4} className='pe-0 text-start'><h5 className="area-text-label">EMS + Cheap Price:</h5></Col>
                                      <Col md={3} className='ps-0 pe-0 text-start'><h5 className="area-text-calc"><span className="ems-grad">{formState.overAllAmountEms}$</span> + <span className="cheap">{formState.cheap}$</span> =</h5></Col>
                                      <Col md={5} className='ps-0 text-start'><h5 className="area-text-value">{formState.cheap + formState.overAllAmountEms}$</h5></Col>
                                    </Row>
                                    <Row className='pt-1'>
                                      <Col md={4} className='pe-0 text-start'><h5 className="area-text-label">EMS + Expensive Price:</h5></Col>
                                      <Col md={3} className='ps-0 pe-0 text-start'><h5 className="area-text-calc"><span className="ems-grad">{formState.overAllAmountEms}$</span> + <span className="exp">{formState.expensive}$</span> =</h5></Col>
                                      <Col md={5} className='ps-0 text-start'><h5 className="area-text-value">{formState.expensive + formState.overAllAmountEms}$</h5></Col>
                                    </Row>
                                  </Col>
                                </Row>
                              </>
                              )}
                            </div>
                          </Col>
                          <Col lg='6' className='ps-0'>
                            <Row className='mt-5 ms-0 me-1'>
                              {!isNewBox ? (
                                <Col md={6}>
                                  <FormGroup className='mb-3'>
                                    <AvField name='boxSize' className="calculator-select" type='select' id='boxSizeID' onChange={(e) => handleCalculate(e)} disabled={isNewBox === true ? true : false}>
                                      <option>Box Size</option>
                                      {boxSizes.map((val, key) => (
                                        <option key={key} value={val.boxSizeNo + '/' + val.volWeight}>
                                          {val.boxSizeNo}
                                        </option>
                                      ))}
                                    </AvField>
                                  </FormGroup>
                                </Col>
                              ) : (
                                <>
                                  <Col md={2}>
                                    <FormGroup className='mb-3'>
                                      <AvField name='L' className="calculator-select" placeholder='Length' autoComplete='off' list='sizes' value='' onChange={(e) => handleCalculate(e)} />
                                    </FormGroup>
                                  </Col>
                                  <Col md={2}>
                                    <FormGroup className='mb-3'>
                                      <AvField name='W' className="calculator-select" placeholder='Width' autoComplete='off' list='sizes' value='' onChange={(e) => handleCalculate(e)} />
                                    </FormGroup>
                                  </Col>
                                  <Col md={2}>
                                    <FormGroup className='mb-3'>
                                      <AvField name='H' className="calculator-select" placeholder='Height' autoComplete='off' list='sizes' value='' onChange={(e) => handleCalculate(e)} />
                                    </FormGroup>
                                  </Col>
                                </>
                              )}
                              <Col md={2} className=''>
                                <div className="calculator-button">
                                  <button type="button" name="" className="btn-work" onClick={() => toggleNewIsNewBox()}>Custom Box </button>
                                </div>
                              </Col>
                              <Col md={4}>
                                <FormGroup className='mb-3'>
                                  <AvField name='netWeight' className="calculator-select" type='select' id='netWeight' value={formState.selectedgrossWeight} required onChange={handleNetWeightCalc}>
                                    <option>Gross Weight</option>
                                    {agentCountriesWeight.map((val, key) => (
                                      <option key={key} value={val.netWeightEms + '/' + val.priceAmountEms}>
                                        {val.netWeightEms}
                                      </option>
                                    ))}
                                  </AvField>
                                </FormGroup>
                              </Col>
                            </Row>
                            <Row className='mt-3 ms-0 me-1'>
                              <Col md={6}>
                                <FormGroup className='mb-3'>
                                  <AvField name='agentCountryPrice' className="calculator-select" type='select' required>
                                    <option>Agent Country</option>
                                    {agentCountries
                                      .sort((a, b) => a.country.localeCompare(b.country))
                                      .map((val, key) => (
                                        <option key={key} value={val.country}>
                                          {val.country}
                                        </option>
                                      ))}
                                  </AvField>
                                </FormGroup>
                              </Col>
                              <Col md={6}>
                                <FormGroup className='mb-3'>
                                  <AvField name='countryPrice' className="calculator-select" type='select' required>
                                    <option>Destination Country</option>
                                    {newcountryPrices
                                      .sort((a, b) => a.countryTo.localeCompare(b.countryTo))
                                      .map((val, key) => (
                                        <option key={key} value={val.countryTo}>
                                          {val.countryTo}
                                        </option>
                                      ))}
                                  </AvField>
                                </FormGroup>
                              </Col>
                            </Row>
                            <Row className='ms-0'>
                              <Col md={8} className="ps-4 pt-4">
                                {formState.boxSizeL && (
                                  <Row className='calculator-text'>
                                    <Col md={1}>
                                      <i className='bx bx-chevron-down-square font-size-20 mt-3 ms-1 me-2' />
                                    </Col>
                                    <Col md={11}>
                                      <h5 className='mt-3'>
                                        : <b>{formState.boxSizeL}cm</b>+<b>{formState.boxSizeW}cm</b> +<b>{formState.boxSizeH}cm</b> =
                                        <b>
                                          {formState.boxSizeL + formState.boxSizeW + formState.boxSizeH}
                                          cm
                                        </b>
                                      </h5>
                                    </Col>
                                  </Row>
                                )}
                              </Col>
                              <Col md={4} className="text-starts">
                                {formState.volumeWeight !== 0 && (
                                  <div>
                                    <img src={box_weight} alt='' />
                                    <div className='weight-block'>
                                      <p>{formState.volumeWeight} kg</p>
                                    </div>
                                    <i className='fas fa-info-circle' data-tip='Chosen weight - the largest amount<br />between Gross Weight and Volume Weight' data-iscapture='true'></i>
                                  </div>
                                )}
                              </Col>
                            </Row>
                            <Row className='ms-0 me-2 mt-4'>
                              <Col md={12}>
                                <div className="calculator-button" >
                                  <button className='btn-work' style={{ width: '100%' }}>Calculate </button>
                                </div>
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                      </AvForm>
                    </div>
                  </Col>
                  <Col lg='2'></Col>
                </Row>
              </div>
              </>
            ) : (
              <Alert color='danger'> You do not have access to this page. </Alert>
            )}
          </Container>
      </div>
    </React.Fragment>
  );
}